import React, { useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import DashboardLayoutWrapper from "commons/wrappers/DashboardLayoutWrapper";
import { DashboardSurveys } from 'features/dashboard/components/DashboardSurveys';
import DashboardStats from 'features/dashboard/components/DashboardStats';
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { styled } from '@mui/system';
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "commons/store/hooks";
import { selectUserProfile } from "core/redux/userSlice";
import {
    COLOR_BG_NAV,
    COLOR_PRIMARY_LIGHT,
    PROJECT_NAME
} from "commons/constants/env";
import { useParams } from 'react-router-dom';
import { roles } from 'commons/auth/roles';
import { useTranslation } from 'react-i18next';

const CustomToggleButton = styled(ToggleButton)(({ theme }) => `
  color: ${theme.palette.secondary.main};
  padding: 6px 16px;
  font-size: 0.875rem;
  border-color: ${theme.palette.primary.light};

  &:hover {
    color: ${theme.palette.primary.dark};
    background: ${COLOR_BG_NAV};
    border-color: ${COLOR_BG_NAV};
  }

  &.Mui-selected {
    color: ${theme.palette.primary.main};
    background: ${theme.palette.primary.light};

    &:hover {
      color: ${theme.palette.primary.dark};
      background: ${COLOR_PRIMARY_LIGHT};
    }
  }
`);

const Dashboard = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userProfile = useAppSelector(selectUserProfile);
    const { type = 'surveys' } = useParams<{
        type: 'surveys' | 'statistics'
    }>();
    const { t } = useTranslation();

    let displayDashboards: boolean = userProfile.roles.some(
        (role: string) => roles.admin.includes(role) || roles.research.includes(role)
    );
    let displayStats: boolean = userProfile.roles.some(
        (role: string) => roles.admin.includes(role) || roles.monitor.includes(role)
    );

    /**
     * This function is used to control actual visible dashboard change if route is changed
     */
    const activeSectionController = () => {
        switch (type) {
            default: {
                if (displayDashboards) {
                  navigate(`/dashboard/${type}`)
                } else {
                  navigate(`/dashboard/surveys`)
                }
                break;
            }
        }
    }

    /**
     * This function is used to change actual section, also will change route
     * @param value
     */
    const changeDashboardTab = (
        value: 'surveys' | 'statistics'
    ) => {
        navigate(`/dashboard/${value}`)
    }

    useEffect(() => {
        document.title = `${PROJECT_NAME} - Dashboard`;
        activeSectionController();

        return () => {
            document.title = PROJECT_NAME;
        };
    }, [dispatch]);

    const renderContent = useCallback(() => {
        switch (type) {
            case 'surveys':
                return (<DashboardSurveys/>);
            case 'statistics':
                return (<DashboardStats/>);
        }
    }, [type]);

    return (
        <DashboardLayoutWrapper>
            <Box mb={1} display="flex" justifyContent="space-between">
                <ToggleButtonGroup
                    size="small"
                    color="secondary"
                    aria-label="outlined primary button group"
                >
                    {displayDashboards && (
                        <CustomToggleButton
                            selected={type === 'surveys'}
                            onClick={() => changeDashboardTab('surveys')}
                            value={type}
                        >
                            {t('Surveys')}
                        </CustomToggleButton>
                    )}

                    {displayStats && (
                        <CustomToggleButton
                            selected={type === 'statistics'}
                            onClick={() => changeDashboardTab('statistics')}
                            value={type}
                        >
                            {t('statistics')}
                        </CustomToggleButton>
                    )}
                </ToggleButtonGroup>
            </Box>
            {renderContent()}
        </DashboardLayoutWrapper>
    );
};

export default Dashboard;